import { useEffect, useState, useRef } from 'react';
import { ArrowLeft, MessageCircle, Users } from 'lucide-react';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { Loader } from './ui/Loader';
import { PageShell } from './layout/PageShell';

interface TalentPackageInterestsViewProps {
  packageId: string;
  packageTitle: string;
  accessToken: string;
  onBack: () => void;
  onViewProfile: (userId: string) => void;
  onStartConversation: (userId: string) => void;
  /** When true (e.g. desktop modal), hide the sub-page header; modal chrome provides close. */
  hideHeader?: boolean;
}

export function TalentPackageInterestsView({
  packageId,
  packageTitle,
  accessToken,
  onBack,
  onViewProfile,
  onStartConversation,
  hideHeader = false,
}: TalentPackageInterestsViewProps) {
  const [loading, setLoading] = useState(true);
  const [interests, setInterests] = useState<any[]>([]);
  const [contactingUserId, setContactingUserId] = useState<string | null>(null);
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const stickyPlaceholderRef = useRef<HTMLDivElement>(null);
  const subPageContentRef = useRef<HTMLDivElement>(null);

  // Mobile: scroll to top when entering Forespørsler screen (main is the scroll container; we don't change currentView so App doesn't scroll)
  useEffect(() => {
    if (hideHeader) return;
    const run = () => {
      const mainEl = typeof document !== 'undefined' ? document.querySelector('main') : null;
      if (mainEl) mainEl.scrollTo({ top: 0, behavior: 'auto' });
      const el = subPageContentRef.current;
      if (el) el.scrollTo(0, 0);
      window.scrollTo({ top: 0, behavior: 'auto' });
    };
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    const t = setTimeout(run, 100);
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(t);
    };
  }, [hideHeader]);

  // iOS PWA: sticky header placeholder so content is not cut off (same pattern as Aktuelle oppdrag / Mine favoritter)
  useEffect(() => {
    if (hideHeader) return;
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const sentinel = stickySentinelRef.current;
    const bar = stickyHeaderRef.current;
    const placeholder = stickyPlaceholderRef.current;
    if (!sentinel || !bar || !placeholder) return;
    const header = document.querySelector('.pwa-nav-top');
    const headerH = header ? header.getBoundingClientRect().height : 56;
    let isStuck = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldStick = !entry.isIntersecting;
        if (shouldStick === isStuck) return;
        isStuck = shouldStick;
        if (shouldStick) {
          const mb = parseFloat(window.getComputedStyle(bar).marginBottom) || 0;
          placeholder.style.height = `${bar.offsetHeight + mb}px`;
          bar.classList.add('is-stuck');
        } else {
          bar.classList.remove('is-stuck');
          placeholder.style.height = '0px';
        }
      },
      { rootMargin: `-${Math.ceil(headerH)}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hideHeader]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/packages/${packageId}/interests`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          toast.error(data?.error || 'Kunne ikke hente forespørsler');
          return;
        }
        setInterests(data.interests || []);
      } catch (error) {
        toast.error('Kunne ikke hente forespørsler');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [accessToken, packageId]);

  const handleStartConversation = async (userId: string) => {
    setContactingUserId(userId);
    try {
      const response = await fetch(`${API_BASE}/packages/${packageId}/contact/${userId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data?.error || 'Kunne ikke starte samtale');
        return;
      }
      setInterests((prev) =>
        prev.map((item) => (item?.user?.id === userId ? { ...item, status: 'contacted' } : item))
      );
      toast.success('Samtale opprettet! Gå til Meldinger for å fortsette');
      onStartConversation(userId);
    } catch (error) {
      toast.error('Kunne ikke starte samtale');
    } finally {
      setContactingUserId(null);
    }
  };

  return (
    <div className="min-h-screen xl:min-h-0 xl:h-full flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50 xl:overflow-hidden">
      {!hideHeader && (
        <>
          <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />
          <div ref={stickyHeaderRef} className="sub-page-header bg-white border-b border-gray-200 px-4 pt-4 pb-3 flex items-center gap-3 flex-shrink-0 sticky top-0 z-20">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 flex items-center gap-3 min-w-0">
              <Users className="w-8 h-8 text-purple-500 flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="font-semibold truncate">Forespørsler</h2>
                <p className="text-sm text-gray-600 truncate">
                  {loading ? '...' : `${interests.length} ${interests.length === 1 ? 'forespørsel' : 'forespørsler'}`} · {packageTitle}
                </p>
              </div>
            </div>
          </div>
          <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />
        </>
      )}

      <div ref={subPageContentRef} className="sub-page-content flex-1 xl:min-h-0 xl:overflow-y-auto">
        <PageShell className="px-4 py-4 xl:max-w-4xl xl:mx-auto xl:py-6">
          {hideHeader && (
            <div className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-200">
              <Users className="w-8 h-8 text-purple-500 flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="font-semibold text-gray-900">Forespørsler</h2>
                <p className="text-sm text-gray-600 truncate">
                  {loading ? '...' : `${interests.length} ${interests.length === 1 ? 'forespørsel' : 'forespørsler'} · ${packageTitle}`}
                </p>
              </div>
            </div>
          )}
          {loading ? (
            <div className="text-center py-12">
              <Loader size={64} className="text-purple-600 mx-auto mb-3" />
              <p className="text-gray-600">Laster forespørsler...</p>
            </div>
          ) : interests.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-gray-700 font-medium">Ingen forespørsler ennå</p>
              <p className="text-gray-500 text-sm mt-1">Nye forespørsler dukker opp her.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {interests.map((interest) => {
                const user = interest?.user || {};
                const status = interest?.status || 'pending';
                return (
                  <div key={interest.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <button
                        onClick={() => user?.id && onViewProfile(user.id)}
                        className="text-left hover:opacity-90 transition-opacity"
                      >
                        <p className="font-semibold text-gray-900">{user?.name || 'Bruker'}</p>
                        <p className="text-sm text-gray-600">{user?.county || 'Ukjent sted'}</p>
                      </button>
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          status === 'contacted' ? 'bg-green-100 text-green-700' : status === 'rejected' ? 'bg-gray-100 text-gray-700' : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {status === 'contacted' ? 'Kontaktet' : status === 'rejected' ? 'Avvist' : 'Venter svar'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap">{interest?.message || ''}</p>
                    <div className="mt-4">
                      <button
                        onClick={() => {
                          if (!user?.id) return;
                          if (status === 'contacted') {
                            onStartConversation(user.id);
                          } else {
                            handleStartConversation(user.id);
                          }
                        }}
                        disabled={!user?.id || (status !== 'contacted' && contactingUserId === user?.id)}
                        className="w-full px-4 py-2.5 rounded-lg font-semibold transition-colors inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        <MessageCircle className="w-4 h-4" />
                        {status === 'contacted'
                          ? 'Gå til samtale'
                          : contactingUserId === user?.id
                          ? 'Starter samtale...'
                          : 'Send melding'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </PageShell>
      </div>
    </div>
  );
}
